import React, { useEffect, useState } from "react";
import { Autocomplete, Avatar, Box, CircularProgress, TextField, Typography, InputAdornment } from "@mui/material";
import SearchRounded from "@mui/icons-material/SearchRounded";
import { getAll } from "../../../services/CollaborateurService";

const useDebounced = (value, delay = 400) => {
    const [v, setV] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setV(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return v;
};

const fullName = (c) => [c?.prenom, c?.nom].filter(Boolean).join(" ");
const initials = (c) => `${c?.prenom?.[0] || ""}${c?.nom?.[0] || ""}`.toUpperCase() || "?";

export default function CollaborateurSearch({ onSelect }) {
    const [search, setSearch] = useState("");
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const debounced = useDebounced(search);

    useEffect(() => {
        let ignore = false;
        setLoading(true);
        getAll({ page: 1, limit: 15, search: debounced, years: [], contrats: [] })
            .then(({ data }) => {
                if (ignore) return;
                setOptions(data?.items || data?.data || data || []);
            })
            .catch(() => !ignore && setOptions([]))
            .finally(() => !ignore && setLoading(false));
        return () => { ignore = true; };
    }, [debounced]);

    return (
        <Autocomplete
            options={options}
            loading={loading}
            filterOptions={(x) => x}
            onChange={(_, value) => onSelect?.(value || null)}
            onInputChange={(_, value, reason) => reason === "input" && setSearch(value)}
            getOptionLabel={(opt) => fullName(opt)}
            isOptionEqualToValue={(a, b) => (a?.id ?? a?.uuid) === (b?.id ?? b?.uuid)}
            noOptionsText="Aucun collaborateur trouvé"
            loadingText="Recherche…"
            renderOption={({ key, ...props }, opt) => (
                <Box component="li" {...props} key={opt?.id || opt?.uuid} sx={{ display: "flex", gap: 1.25 }}>
                    <Avatar sx={{ width: 30, height: 30, fontSize: "0.72rem", fontWeight: 700, bgcolor: "#ebf4fb", color: "#03407b" }}>{initials(opt)}</Avatar>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: "#0f172a" }}>{fullName(opt)}</Typography>
                        <Typography sx={{ fontSize: "0.75rem", color: "#64748b" }}>{opt?.email || opt?.fonction || "—"}</Typography>
                    </Box>
                </Box>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    size="small"
                    placeholder="Rechercher un collaborateur (nom, email)…"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", background: "#fff" } }}
                    slotProps={{
                        ...params.slotProps,
                        input: {
                            ...params.slotProps?.input,
                            startAdornment: (
                                <InputAdornment position="start"><SearchRounded sx={{ color: "#94a3b8" }} /></InputAdornment>
                            ),
                            endAdornment: (
                                <>
                                    {loading ? <CircularProgress size={16} /> : null}
                                    {params.slotProps?.input?.endAdornment}
                                </>
                            ),
                        },
                    }}
                />
            )}
        />
    );
}
